import { ChangeDetectorRef, Component } from '@angular/core';
import { UserRequest } from './user.model';
import { UserService } from './user.service';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-user',
  standalone: false,
  templateUrl: './user.component.html',
  styleUrl: './user.component.css'
})
export class UserComponent {

  successMessage = '';
  errorMessage = '';

  users: any[] = [];
  filteredUsers: any[] = [];
  roles: any[] = [];

  currentPage = 1;
  pageSize = 5;

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  userId = 0;
  email = '';
  password = '';
  roleId = 1;
  isEnabled = true;
  is2FAEnabled = false;
  firstName = '';
  lastName = '';
  phoneNumber = '';
  address = '';
  dateOfBirth = '';

  searchText = '';

  constructor(
    private userService: UserService,
    private roleService: RoleService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadRoles();
    this.loadUsers();
  }

  loadRoles() {
    this.roleService.getRoles().subscribe((res: any) => {
      const apiData: any[] = res.data || [];
      const roleMap = new Map<number, any>();

      apiData.forEach((role: any) => {
        const existing = roleMap.get(role.roleId);
        if (!existing || role.languageCode === 'kh' || (!existing.name && role.languageCode === 'en')) {
          roleMap.set(role.roleId, {
            roleId: role.roleId,
            name: role.name,
            languageCode: role.languageCode
          });
        }
      });

      this.roles = Array.from(roleMap.values()).sort((a, b) => a.roleId - b.roleId);
      if (this.roles.length > 0 && !this.roles.some(role => role.roleId === this.roleId)) {
        this.roleId = this.roles[0].roleId;
      }
    });
  }

  loadUsers() {
    this.userService.getAllUsers().subscribe((res: any) => {
      const apiData: any[] = res.data || [];
      this.users = apiData.map(item => ({
        userId: item.userId,
        email: item.email,
        roleId: item.roleId,
        isEnabled: item.isEnabled,
        is2FAEnabled: item.is2FAEnabled,
        firstName: item.firstName,
        lastName: item.lastName,
        phoneNumber: item.phoneNumber,
        address: item.address,
        dateOfBirth: item.dateOfBirth
      }));

      this.filterUsers();
    });
  }

  filterUsers() {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    if (!keyword) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter(user =>
      `${user.email} ${user.firstName ?? ''} ${user.lastName ?? ''} ${user.phoneNumber ?? ''}`
        .toLowerCase()
        .includes(keyword)
    );
  }

  get pagedUsers(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredUsers.slice(start, end);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  totalPages(): number {
    return Math.ceil(this.filteredUsers.length / this.pageSize);
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.userId = id;

    this.userService.getUserById(id).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;

          this.userId = data.userId;
          this.email = data.email ?? '';
          this.password = '';
          this.roleId = data.roleId ?? 1;
          this.isEnabled = data.isEnabled;
          this.is2FAEnabled = data.is2FAEnabled;
          this.firstName = data.firstName ?? '';
          this.lastName = data.lastName ?? '';
          this.phoneNumber = data.phoneNumber ?? '';
          this.address = data.address ?? '';
          this.dateOfBirth = data.dateOfBirth ? data.dateOfBirth.substring(0, 10) : '';

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: () => {
        this.errorMessage = 'Failed to load user data';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  getRequest(): UserRequest {
    return {
      email: this.email ?? '',
      password: this.password ?? '',
      roleId: Number(this.roleId),
      isEnabled: this.isEnabled,
      is2FAEnabled: this.is2FAEnabled,
      firstName: this.firstName ?? '',
      lastName: this.lastName ?? '',
      phoneNumber: this.phoneNumber ?? '',
      address: this.address ?? '',
      dateOfBirth: this.dateOfBirth || null
    };
  }

  resetForm() {
    this.userId = 0;
    this.email = '';
    this.password = '';
    this.roleId = 1;
    this.isEnabled = true;
    this.is2FAEnabled = false;
    this.firstName = '';
    this.lastName = '';
    this.phoneNumber = '';
    this.address = '';
    this.dateOfBirth = '';
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadUsers();
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    if (!this.password) {
      this.errorMessage = 'Password is required';
      return;
    }

    const request = this.getRequest();
    this.clearMsg();

    this.userService.create(request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  update() {
    if (!this.password) {
      this.errorMessage = 'Password is required by the current API when updating a user';
      return;
    }

    this.clearMsg();

    const request = this.getRequest();
    this.userService.update(this.userId, request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.userService.delete(this.userId).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  getFullName(user: any): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'N/A';
  }

  getRoleName(roleId: number): string {
    return this.roles.find(role => role.roleId === roleId)?.name || `Role ${roleId}`;
  }
}
